import type { FunctionComponent } from 'react'

import {
	BodySStrong,
	H2,
	PrimaryButton,
	SecondaryButton,
	SPACING,
} from '@monthio/design-system'
import {
	BoxInput,
	InfoTooltip,
	TextInputField,
	useModal,
} from '@monthio/react-components'
import dayjs from 'dayjs'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import type {
	ExtraOrdinaryItemGroupType,
	SavingsCapacityExtraordinaryItemGroupDto,
} from '../SavingsCapacity.types'

import { ContinueWithoutSelectionModal } from './ContinueWithoutSelectionModal'
import { ExtraordinaryItemsGroupBasePage } from './ExtraordinaryItemsGroupBasePage'
import { CONTINUE_WITHOUT_SELECTION_MODAL_ID } from './ExtraordinaryItemsGroupPage'
import { generateRelevantMonths, MonthPicker } from './MonthPicker'
import { InputContainer, InputRowContainer } from './SharedStyledComponents'

const FormContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${SPACING.xs};
`

const DatePeriodWrapper = styled.div`
	display: flex;
	flex-direction: row;
	gap: ${SPACING.xs};
	justify-content: flex-end;
	align-items: center;
`
interface ManualEntryFormProps {
	onNext: (
		extraordinaryItemGroup: SavingsCapacityExtraordinaryItemGroupDto,
	) => void
	onPrevious: () => void
	type: ExtraOrdinaryItemGroupType
}

type ModalType = 'missingInputs' | 'noInputs'

export const ManualEntryForm: FunctionComponent<ManualEntryFormProps> = ({
	onNext,
	onPrevious,
	type,
}) => {
	const { t } = useTranslation()
	const [amount, setAmount] = useState<number>()
	const [isDateRange, setIsDateRange] = useState(false)
	const [notes, setNotes] = useState<string>()
	const [startDate, setStartDate] = useState<dayjs.Dayjs>()
	const [endDate, setEndDate] = useState<dayjs.Dayjs>()

	const { openModal, closeModal } = useModal()

	useEffect(() => {
		if (!startDate || !endDate) return
		const isEndDateBeforeStart = endDate.isBefore(startDate)

		if (isEndDateBeforeStart) {
			setEndDate(undefined)
		}
	}, [endDate, startDate])

	const handleContinueWithoutSelection = useCallback(() => {
		onNext({
			type,
			items: [],
			notes: '',
		} as SavingsCapacityExtraordinaryItemGroupDto)
		closeModal(CONTINUE_WITHOUT_SELECTION_MODAL_ID)
	}, [closeModal, onNext, type])

	const renderContinueWithoutSelectionModalBody = useCallback(
		(modalType: ModalType) => (
			<ContinueWithoutSelectionModal
				onContinue={handleContinueWithoutSelection}
				text={
					modalType === 'missingInputs'
						? t('savingsCapacity-continueManualAddModal-missingInputs-subtitle')
						: t('savingsCapacity-continueManualAddModal-noInputs-subtitle')
				}
			/>
		),
		[handleContinueWithoutSelection, t],
	)

	const openContinueWithoutSelectionModal = useCallback(
		(modalType: ModalType) => {
			openModal({
				id: CONTINUE_WITHOUT_SELECTION_MODAL_ID,
				title:
					modalType === 'missingInputs'
						? t('savingsCapacity-continueManualAddModal-missingInputs-title')
						: t('savingsCapacity-continueManualAddModal-noInputs-title'),
				responsive: false,
				closable: true,
				bodyRender: () => renderContinueWithoutSelectionModalBody(modalType),
			})
		},
		[openModal, t, renderContinueWithoutSelectionModalBody],
	)

	const handleSave = () => {
		const isNotesFilled = notes !== undefined && notes !== ''
		const isAmountFilled = amount !== undefined
		const isStartDateFilled = startDate !== undefined
		const isEndDateFilledOrNotRequired =
			!isDateRange || (isDateRange && endDate !== undefined)

		const canSave =
			isNotesFilled &&
			isAmountFilled &&
			isStartDateFilled &&
			isEndDateFilledOrNotRequired

		const isEmpty =
			!isNotesFilled &&
			!isAmountFilled &&
			!isStartDateFilled &&
			(!isDateRange || (isDateRange && endDate === undefined))

		if (!canSave) {
			openContinueWithoutSelectionModal(isEmpty ? 'noInputs' : 'missingInputs')
			return
		}

		onNext({
			notes,
			type,
			items: [
				{
					type: 'manual',
					amount,
					description: '',
					startDate: startDate.toDate(),
					endDate: endDate?.toDate() ?? undefined,
				},
			],
		})
	}

	const relevantMonths = generateRelevantMonths()

	const getEndDateMonths = useCallback(() => {
		if (!isDateRange || !startDate) return relevantMonths

		return relevantMonths.filter((date) => {
			const dayjsDate = dayjs(date)

			return startDate.isBefore(dayjsDate)
		})
	}, [isDateRange, relevantMonths, startDate])

	return (
		<ExtraordinaryItemsGroupBasePage
			buttons={[
				<PrimaryButton key="save" onClick={handleSave} size="xl">
					{t('savingsCapacity-extraordinaryItemsTransactions-save-button')}
				</PrimaryButton>,
			]}
			onGoBackClick={onPrevious}
			category={type}
			subtitle={t(
				'savingsCapacity-extraordinaryItemsTransactions-manualEntry-subtitle',
			)}
		>
			<FormContainer>
				<InputContainer>
					<InputRowContainer>
						<InfoTooltip
							tooltip={t(
								'savingsCapacity-extraordinaryItemsTransactions-manualEntry-amount-tooltip',
							)}
							variant="helper"
						>
							<H2>
								{t(
									'savingsCapacity-extraordinaryItemsTransactions-manualEntry-amount-label',
								)}
							</H2>
						</InfoTooltip>
						<BoxInput
							value={amount?.toString() || ''}
							onChange={(e) => setAmount(Number(e.target.value))}
							currencyInputProps={{
								allowDecimals: false,
								allowNegativeValue: false,
								maxLength: 8,
							}}
						/>
					</InputRowContainer>
				</InputContainer>

				<InputContainer>
					<InputRowContainer>
						<InfoTooltip
							tooltip={t(
								'savingsCapacity-extraordinaryItemsTransactions-manualEntry-date-tooltip',
							)}
							variant="helper"
						>
							<H2>
								{t(
									'savingsCapacity-extraordinaryItemsTransactions-manualEntry-date-label',
								)}
							</H2>
						</InfoTooltip>

						{isDateRange ? (
							<DatePeriodWrapper>
								<MonthPicker
									handleChange={(value: string) => setStartDate(dayjs(value))}
									month={startDate ? startDate?.month() + 1 : undefined}
									options={relevantMonths}
									year={startDate?.year()}
								/>

								{t(
									'savingsCapacity-extraordinaryItemsTransactions-manualEntry-dateTo-label',
								)}
								<MonthPicker
									handleChange={(value: string) => setEndDate(dayjs(value))}
									month={endDate ? endDate?.month() + 1 : undefined}
									options={getEndDateMonths()}
									year={endDate?.year()}
								/>
							</DatePeriodWrapper>
						) : (
							<MonthPicker
								handleChange={(value: string) => setStartDate(dayjs(value))}
								month={startDate ? startDate?.month() + 1 : undefined}
								options={relevantMonths}
								year={startDate?.year()}
							/>
						)}
					</InputRowContainer>
					<SecondaryButton onClick={() => setIsDateRange(!isDateRange)}>
						{t(
							isDateRange
								? 'savingsCapacity-extraordinaryItemsTransactions-singleDate-toggle'
								: 'savingsCapacity-extraordinaryItemsTransactions-dateRange-toggle',
						)}
					</SecondaryButton>
				</InputContainer>
			</FormContainer>
			<div>
				<BodySStrong>
					{t(
						'savingsCapacity-extraordinaryItemsTransactions-manualEntry-comment-label',
					)}
				</BodySStrong>
				<InputContainer>
					<TextInputField
						placeholder={t(
							'savingsCapacity-extraordinaryItemsTransactions-manualEntry-comment-placeholder',
						)}
						value={notes}
						multiline
						rows={4}
						onChange={(e) => setNotes(e.target.value)}
						InputProps={{ disableUnderline: true }}
					/>
				</InputContainer>
			</div>
		</ExtraordinaryItemsGroupBasePage>
	)
}
